/**
 * CommonAnalyticsBroker
 */
const manifest = require('manifest');
const assign = require('object-assign');
const GAS_SINGLE_EVENT_ENDPOINT = 'https://mgas.atlassian.io/v1/event';
const Beacon = require('./beacon');
const ErrorEventProperties = require('./error-event-properties');
const isDebuggingEnabled = require('./is-debugging-enabled');

function generateUUID() {
  let octets;

  if ((window.crypto || window.msCrypto || {}).getRandomValues) {
    octets = (window.crypto || window.msCrypto).getRandomValues(
      new Uint8Array(16)
    ).reduce((accum, value) => {
      accum.push(value >>> 4, value & 0x0F);
      return accum;
    }, []);
  } else {
    // Use Math.random();
    octets = new Array(32);
    for (let i = 0; i < octets.length; i++) {
      octets[i] = Math.random() * 16 | 0
    }
  }

  let i = 0;
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c =>
    (c === "x" ? octets[i++] : (octets[i++] & 0x3 | 0x8)).toString(16)
  );
}

export default class CommonAnalyticsBroker {
  constructor() {
    this._defaults = {
      __cohortId: manifest.cohortId,
      __product: manifest.product,
      __guid: generateUUID() // GUID is used to provide a unique ID for collating events sent during the same page load
    };
    this._url = GAS_SINGLE_EVENT_ENDPOINT;
    this.eventPrefix = manifest.eventPrefix;
  }

  createRequest(name, properties = {}) {
    return this._isGas() ? this.createGasRequest(name, properties) : this.createCloudRequest(name, properties);
  }

  /**
   * Create a request which is suitable for the cloud-analytics plugin's "bulk" endpoint.
   */
  createCloudRequest(name, properties = {}) {
    return [{name, properties, timeDelta: 0}];
  };

  /**
   * Create a request which is suitable for GAS's single event endpoint.
   */
  createGasRequest(name, properties = {}) {
    return {
      name,
      server: this.group || window.location.hostname,
      product: this.product || 'unknown',
      user: this.user || '-',
      serverTime: new Date().valueOf(),
      properties
    };
  }

  setContext({user, product, group, cloudBaseUrl}) {
    this.user = user;
    this.product = product;
    this.group = group;
    if (typeof cloudBaseUrl === 'string') {
      this._url = `${cloudBaseUrl}/rest/analytics/1.0/publish/bulk`;
    } else {
      this._url = GAS_SINGLE_EVENT_ENDPOINT;
    }
  }

  /**
   * Set default properties to be sent with any analytics event.
   *
   * Properties in the specified default will overwrite
   * any current defaults.
   */
  assignDefaultProperties(defaults) {
    assign(this._defaults, defaults);
  }

  /**
   * Get the current defaults.
   */
  getDefaultProperties() {
    return assign({}, this._defaults);
  }

  /**
   * Send the analytics event.
   */
  send(name = 'unknown', properties = {}) {
    // The hardcoded "grow0." ensures that the analytic event passes the whitelist on the server,
    // regardless of what the experiment's actual experiment ID is.
    const eventName = `${this.eventPrefix}.${name}`;
    const mixedProperties = assign(this.getDefaultProperties(), properties);
    if (isDebuggingEnabled()) {
      console.log(`%c${JSON.stringify({name: eventName, properties: mixedProperties}, null, 2)}`, "color: blue; font-size: small");
    }

    if (process.env.ENV !== 'production') {
      return;
    }

    let successfullyQueued = false;
    if (Beacon.isSupported()) {
      const request = this.createRequest(eventName, mixedProperties);
      successfullyQueued = this._isGas() ? Beacon.sendAsJsonText(this._url, request) : Beacon.sendAsJson(this._url, request);
    }
    if (!successfullyQueued) {
      const request = this.createRequest(eventName, assign(mixedProperties, {__ajax: true}));
      fetch(this._url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify(request)
      }).catch(e => {
        setTimeout(() => {
          e.message = 'There was an error sending an analytics event: ' + e.message;
          throw e;
        }, 0);
      });
    }
  }

  /**
   * Send the specified error as an analytics event.
   *
   * Note: Only do this in production environments.
   */
  error(e) {
    if (process.env.ENV === 'production') {
      this.send('log.error', ErrorEventProperties.create(e));
    }
  }

  _isGas() {
    return this._url === GAS_SINGLE_EVENT_ENDPOINT;
  }
}



// WEBPACK FOOTER //
// ./~/@atlassiansox/experiment-common-core/lib/common-analytics-broker.js